import React, { useState } from 'react';
import { Box, Typography, Container, Alert, Paper, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BulkWizardUpload, { SuccessData } from '../../components/Wizard/components/BulkWizardUpload';
import BulkWizardTemplate from '../../components/Wizard/components/BulkWizardTemplate';

function BulkWizardPage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<SuccessData[]>([]);

  const handleError = (error: string) => {
    setSuccessData([]);
    setErrorMessage(error);
  };

  const handleClearError = () => {
    setErrorMessage(null);
  };

  const handleSuccess = (data: SuccessData[]) => {
    setSuccessData(data);
    setErrorMessage(null);
  };

  const isErrorMessageArray = errorMessage && Array.isArray(errorMessage);
  const isErrorMessageString = errorMessage && typeof errorMessage === 'string';

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Bulk Campaign Wizard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload a CSV file to create multiple campaigns with offers and traffic sources at once.
        </Typography>

        {isErrorMessageString && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={handleClearError}>
            {errorMessage}
          </Alert>
        )}

        {isErrorMessageArray && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={handleClearError}>
            {errorMessage.map((item, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                {item}
              </Typography>
            ))}
          </Alert>
        )}

        <Box my={2} display="flex" gap={2} flexDirection="column">
          <Box display="flex" gap={2}>
            <BulkWizardTemplate />
            <BulkWizardUpload onError={handleError} onClearError={handleClearError} onSuccess={handleSuccess} />
          </Box>
        </Box>

        {successData.length > 0 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Successful
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The bulk upload was successful. Created resources:
            </Typography>
            {successData.map((item, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {item.trafficSource && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Traffic Source:
                      </Typography>
                      <Link component={RouterLink} to={`/traffic/${item.trafficSource._id}`} target="_blank">
                        {item.trafficSource.name}
                      </Link>
                    </Box>
                  )}
                  {item.campaign && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Campaign:
                      </Typography>
                      <Link component={RouterLink} to={`/campaigns/${item.campaign._id}/offers`} target="_blank">
                        {item.campaign.name}
                      </Link>
                      <Typography variant="body2">{item.campaign.entryDomain}</Typography>
                    </Box>
                  )}
                  {item.offer && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Offer:
                      </Typography>
                      <Typography variant="body2">{item.offer.name}</Typography>
                      <Typography variant="body2">{item.offer.offerLink}</Typography>
                    </Box>
                  )}
                  {item.campaignOffer && item.campaignOffer.affiliateUrl && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Affiliate URL:
                      </Typography>
                      <Link href={item.campaignOffer.affiliateUrl} target="_blank">
                        {item.campaignOffer.affiliateUrl}
                      </Link>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default BulkWizardPage;
